import React, { useRef, useCallback, useEffect, useMemo } from "react";
import anime from "animejs";
import { createButtonPressAnimation, attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Text, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle } from "pixi.js";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { FONT_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { useDispatch, useSelector } from "react-redux";
import {
  openAutoplayPopup,
  closeAutoplayPopup,
  stopAutoPlay,
  closeMenu,
} from "../../../../State/actions";
import {
  selectAutoPlayActive,
  selectRemainingAutoSpins,
  selectIsSpinning,
  selectAutoplayPopupActive,
  selectCriticalAnimationPlaying,
  selectHoldAndWinActive,
} from "../../../../State/selectors";

export const POPI_AUTOPLAY_BUTTON_CONFIG = {
  // Button base dimensions
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,

  // Background base dimensions
  BACKGROUND_BASE_WIDTH: 80,
  BACKGROUND_BASE_HEIGHT: 152,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.965,
        backgroundMultiplier: 1.5,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.75,
        backgroundMultiplier: 1.325,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
        backgroundMultiplier: 1.75,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.7,
        backgroundMultiplier: 1.275,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
      backgroundMultiplier: 0.8,
    },
  },
};

const ButtonAutoplay = ({ toolbar }) => {
  const dispatch = useDispatch();
  const {
    getResponsiveConfig,
    device,
    orientation,
    dimensions,
    uiSymbolScale,
  } = useDimensions();

  // Redux state
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const remainingAutoSpins = useSelector(selectRemainingAutoSpins);
  const popupActive = useSelector(selectAutoplayPopupActive);
  const isSpinning = useSelector(selectIsSpinning);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);
  const backgroundSpriteRef = useRef(null);
  const backgroundAnimationRef = useRef(null);
  const widthAnimationRef = useRef(null);
  const textRef = useRef(null);
  const previousIsSpinningRef = useRef(isSpinning);

  // Track if press animation is currently running (prevents rapid click shrinking)
  const isPressAnimatingRef = useRef(false);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  // Add ref to track if button is currently processing a click
  const isProcessingClickRef = useRef(false);

  // Add ref to store timeout ID for proper cleanup
  const debounceTimeoutRef = useRef(null);

  const textureInactive = PIXI.Assets.get(ASSET_NAME.icon_autoplay);
  const textureActive = PIXI.Assets.get(ASSET_NAME.icon_autoplay_active);
  const textureBackground = PIXI.Assets.get(
    ASSET_NAME.icon_autoplay_background
  );

  // Use active texture when autoplay is active, otherwise use inactive
  const texture = autoPlayActive ? textureActive : textureInactive;

  // Disable button when: critical animation is playing OR (spin is active AND autoplay is not active) OR hold and win is active
  const isDisabled =
    criticalAnimationPlaying ||
    (isSpinning && !autoPlayActive) ||
    holdAndWinActive;

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(POPI_AUTOPLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Calculate background config from unified config
  const buttonBackgroundConfig = useMemo(() => {
    // Use same scaling logic as getResponsiveConfig
    const symbolScale = uiSymbolScale; // Same as getResponsiveConfig uses for useUIDimensions=true
    const backgroundMultiplier = buttonConfig.backgroundMultiplier || 1.0;

    return {
      width:
        POPI_AUTOPLAY_BUTTON_CONFIG.BACKGROUND_BASE_WIDTH *
        backgroundMultiplier *
        symbolScale,
      height:
        POPI_AUTOPLAY_BUTTON_CONFIG.BACKGROUND_BASE_HEIGHT *
        backgroundMultiplier *
        symbolScale,
    };
  }, [buttonConfig, uiSymbolScale]);

  // Get text configuration (font multiplier, x, y positions)
  const getTextConfig = () => {
    let fontMultiplier = 1.0;
    let x = 0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.6;
        x = buttonBackgroundConfig.width / 2;
        y = buttonBackgroundConfig.height * 0.7;
      } else {
        fontMultiplier = 1.45;
        x = -buttonBackgroundConfig.width * 1.4;
        y = buttonBackgroundConfig.height * 0.26;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.85;
        x = buttonBackgroundConfig.width / 2;
        y = buttonBackgroundConfig.height * 0.7;
      } else {
        fontMultiplier = 1.4;
        x = -buttonBackgroundConfig.width * 1.4;
        y = buttonBackgroundConfig.height * 0.275;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      x = -buttonBackgroundConfig.width * 1.4;
      y = buttonBackgroundConfig.height * 0.275;
    }

    return { fontMultiplier, x, y };
  };

  // Calculate responsive button position based on device and orientation - memoized
  // Uses toolbar width instead of full screen width
  const buttonPosition = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          button: {
            x: toolbar.width / 2 - buttonConfig.width * 1.65,
            y: -dimensions.height / 5.2,
          },
          background: {
            x: toolbar.width / 2 - buttonConfig.width * 2.035,
            y: -dimensions.height / 4.75,
          },
        };
      } else {
        return {
          button: {
            x: toolbar.width - buttonConfig.width * 0.775,
            y: -dimensions.height / 2 + buttonConfig.height * 2.65,
          },
          background: {
            x: toolbar.width - buttonConfig.width * 0.5,
            y: -dimensions.height / 2 + buttonConfig.height * 2.275,
          },
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          button: {
            x: toolbar.width / 2 - buttonConfig.width * 1.675,
            y: -dimensions.height / 5.1,
          },
          background: {
            x: toolbar.width / 2 - buttonConfig.width * 2.057,
            y: -dimensions.height / 4.5,
          },
        };
      } else {
        return {
          button: {
            x: toolbar.width - buttonConfig.width * 3.15,
            y: toolbar.height / 2,
          },
          background: {
            x: toolbar.width - buttonConfig.width * 2.9,
            y: toolbar.height / 2 - buttonBackgroundConfig.height * 0.265,
          },
        };
      }
    } else {
      return {
        button: {
          x: toolbar.width - buttonConfig.width * 4.4,
          y: toolbar.height / 2,
        },
        background: {
          x: toolbar.width - buttonConfig.width * 4.1,
          y: toolbar.height / 2 - buttonBackgroundConfig.height * 0.265,
        },
      };
    }
  }, [
    device,
    orientation,
    dimensions.height,
    toolbar.width,
    toolbar.height,
    buttonConfig.width,
    buttonConfig.height,
    buttonBackgroundConfig.height,
  ]);

  // Calculate background rotation: 0 for portrait, 90deg for landscape/desktop
  const backgroundRotation =
    orientation === PixiSettings.ORIENTATION.PORTRAIT ? 0 : Math.PI / 2;

  // Remaining autospins count from Redux
  const isInfinity = remainingAutoSpins === Infinity;
  const displayRemainingSpins = isInfinity
    ? null
    : remainingAutoSpins.toString();

  // Get infinity icon texture
  const infinityTexture = PIXI.Assets.get(ASSET_NAME.icon_infinity);

  // Text configuration
  const textConfig = getTextConfig();
  const baseFontSize = 26;
  const responsiveFontSize = baseFontSize * textConfig.fontMultiplier;
  const FONT_SIZE = Math.floor(responsiveFontSize * uiSymbolScale);

  // Calculate text/icon position relative to background
  const textPosition = {
    x: buttonPosition.background.x + textConfig.x,
    y: buttonPosition.background.y + textConfig.y,
  };

  // Calculate infinity icon size maintaining aspect ratio
  const infinityIconSize = useMemo(() => {
    if (!isInfinity || !infinityTexture) return { width: 0, height: 0 };

    const iconAspectRatio = infinityTexture.width / infinityTexture.height;
    const maxSize = FONT_SIZE * 1.2;

    // Use maxSize as width, calculate height to maintain aspect ratio
    const iconWidth = maxSize;
    const iconHeight = iconWidth / iconAspectRatio;

    return { width: iconWidth, height: iconHeight };
  }, [isInfinity, infinityTexture, FONT_SIZE]);

  // Text style for remaining autospins
  const autospinsTextStyle = new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: FONT_SIZE,
    fontWeight: "500",
    fill: "#ffffff",
    align: "center",
  });

  const handleClick = () => {
    // Close menu if open
    dispatch(closeMenu());

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    // Disable button when disabled (critical animation OR spin active without autoplay)
    if (isDisabled) {
      return;
    }

    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    // Add press animation
    if (buttonSpriteRef.current && !isPressAnimatingRef.current) {
      isPressAnimatingRef.current = true;
      createButtonPressAnimation(buttonSpriteRef.current, {
        targetWidth: buttonConfig.width,
        targetHeight: buttonConfig.height,
        onComplete: () => {
          isPressAnimatingRef.current = false;
        }
      });
    }

    if (autoPlayActive) {
      // If autoplay is active, stop it
      dispatch(stopAutoPlay());
    } else {
      // If autoplay is inactive, toggle popup
      if (popupActive) {
        dispatch(closeAutoplayPopup());
      } else {
        dispatch(openAutoplayPopup());
      }
    }

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    // Don't apply hover effect if button is disabled
    if (isDisabled) {
      return;
    }
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [buttonSpriteRef, brightnessFilter, isDisabled]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, [buttonSpriteRef]);

  // Initialize background height and text alpha on mount ONLY
  useEffect(() => {
    if (backgroundSpriteRef.current && !backgroundSpriteRef.current._initialized) {
      backgroundSpriteRef.current.height = 0;
      backgroundSpriteRef.current.alpha = 1;
      backgroundSpriteRef.current._originalHeight =
        buttonBackgroundConfig.height;
      // Store original width for animation
      backgroundSpriteRef.current._originalWidth = buttonBackgroundConfig.width;
      backgroundSpriteRef.current._initialized = true;
    }
    if (textRef.current && !textRef.current._initialized) {
      textRef.current.alpha = 1;
      textRef.current._initialized = true;
    }
  }, [buttonBackgroundConfig.height, buttonBackgroundConfig.width]);

  // Animate background height and alpha based on autoPlayActive state
  // Only expand background when autoplay is running, not when popup opens
  useEffect(() => {
    if (!backgroundSpriteRef.current || !textRef.current) return;

    const backgroundSprite = backgroundSpriteRef.current;
    const textElement = textRef.current;

    // Store original height if not already stored
    if (backgroundSprite._originalHeight === undefined) {
      backgroundSprite._originalHeight = buttonBackgroundConfig.height;
    }

    // Stop any existing animation
    if (backgroundAnimationRef.current) {
      backgroundAnimationRef.current.pause();
    }

    // Only expand background when autoplay is active (running)
    const targetHeight = autoPlayActive ? buttonBackgroundConfig.height : 0;
    const isClosing = !autoPlayActive;

    // Animate height
    anime({
      targets: backgroundSprite,
      height: targetHeight,
      duration: 300,
      easing: "easeOutCubic",
    });

    // Animate alpha - background and text have separate timings
    if (isClosing) {
      // Background fades out with delay
      anime({
        targets: backgroundSprite,
        alpha: 0,
        delay: 35,
        duration: 20,
        easing: "easeOutCubic",
      });
      // Text fades out instantly (no delay)
      anime({
        targets: textElement,
        alpha: 0,
        delay: 0,
        duration: 20,
        easing: "easeOutCubic",
      });
    } else {
      // Fade in when opening - set initial alpha via ref, not prop
      requestAnimationFrame(() => {
        if (backgroundSprite) backgroundSprite.alpha = 0;
        if (textElement) textElement.alpha = 0;
      });
      // Background fades in with delay
      anime({
        targets: backgroundSprite,
        alpha: 1,
        delay: 25,
        duration: 100,
        easing: "easeOutCubic",
      });
      // Text fades in with delay (appears after background)
      anime({
        targets: textElement,
        alpha: 1,
        delay: 100,
        duration: 100,
        easing: "easeOutCubic",
      });
    }
  }, [autoPlayActive, buttonBackgroundConfig.height]);

  // Animate background height when a new spin starts during autoplay
  useEffect(() => {
    // Only animate if autoplay is active and spin just started (transitioned from false to true)
    if (
      !autoPlayActive ||
      !backgroundSpriteRef.current ||
      !isSpinning ||
      previousIsSpinningRef.current === isSpinning
    ) {
      previousIsSpinningRef.current = isSpinning;
      return;
    }

    const backgroundSprite = backgroundSpriteRef.current;
    const originalHeight =
      backgroundSprite._originalHeight || buttonBackgroundConfig.height;

    // Stop any existing height animation
    if (widthAnimationRef.current) {
      widthAnimationRef.current.pause();
    }

    // Store original height if not already stored
    if (backgroundSprite._originalHeight === undefined) {
      backgroundSprite._originalHeight = originalHeight;
    }

    // Animate: shrink height then bounce back with elastic easing
    widthAnimationRef.current = anime({
      targets: backgroundSprite,
      height: [originalHeight * 0.8, originalHeight],
      duration: 400,
      easing: "easeOutElastic(1, 0.5)",
    });

    // Update previous value
    previousIsSpinningRef.current = isSpinning;
  }, [isSpinning, autoPlayActive, buttonBackgroundConfig.height]);

  // Remove hover effects and manage alpha when button becomes disabled
  useEffect(() => {
    if (buttonSpriteRef.current) {
      if (isDisabled) {
        buttonSpriteRef.current.filters = [];
        isHoveredRef.current = false;
        buttonSpriteRef.current.alpha = 0.7;
      } else {
        buttonSpriteRef.current.alpha = 1;
      }
    }
  }, [isDisabled]);

  // Clean up hover effects and debounce timeout on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
      // Clear any pending debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(() => buttonConfig.width / 2, [buttonConfig.width]);

  return (
    <Container name="secondary_button_autoplay" alpha={!holdAndWinActive ? 1 : 0.001}>
      <Sprite
        ref={backgroundSpriteRef}
        texture={textureBackground}
        name="button_autoplay_background"
        anchor={0}
        width={buttonBackgroundConfig.width}
        x={buttonPosition.background.x}
        y={buttonPosition.background.y}
        rotation={backgroundRotation}
        resolution={2}
      />
      {isInfinity && infinityTexture ? (
        <Sprite
          ref={textRef}
          texture={infinityTexture}
          anchor={0.5}
          width={infinityIconSize.width}
          height={infinityIconSize.height}
          x={textPosition.x}
          y={textPosition.y}
          resolution={2}
        />
      ) : (
        <Text
          ref={textRef}
          text={displayRemainingSpins}
          style={autospinsTextStyle}
          anchor={0.5}
          x={textPosition.x}
          y={textPosition.y}
          resolution={2}
        />
      )}
      <Container x={buttonPosition.button.x} y={buttonPosition.button.y}>
        {/* Interactive circular area - handles all pointer events */}
        <Graphics
          name="button_autoplay_interactive_area"
          eventMode={isDisabled ? "none" : "static"}
          cursor={isDisabled ? "default" : "pointer"}
          hitArea={new PIXI.Circle(0, 0, buttonRadius)}
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          draw={(g) => {
            g.clear();
            // Nearly invisible fill (required for hit detection to work)
            g.beginFill(0x000000, 0.01);
            g.drawCircle(0, 0, buttonRadius);
            g.endFill();
          }}
        />

        {/* Visual sprite (non-interactive, just for display) */}
        <Sprite
          ref={buttonSpriteRef}
          texture={texture}
          name="button_autoplay_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode="none"
          alpha={isDisabled ? 0.5 : 1}
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default ButtonAutoplay;
